import { useCallback, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { EBundlesViewModel } from '@appcharge/shared-ui';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import {
  EBadgeType,
  Offer
} from 'common/contracts';
import {
  EFeatureFlag,
  EProductPriority,
  EPromotionsFormState,
  ESaleDiscountDisplayType,
  ESpecialOfferInternalViewModel,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { Promotions } from 'design-system/AcSidebar/constants';
import { useFormik } from 'formik';
import { useTabTracking } from 'hooks/useTabTracking';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { convertProgressBarPointsToApiFormat } from 'utils/barPointsUtils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import { IProductProps, ProductProps } from 'views/Offers/Offer.types';
import { useBadgeForm } from 'views/Offers/hooks/useBadgeForm';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormData } from 'views/Offers/hooks/useOfferFormData';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { useProgressBarForm } from 'views/Offers/hooks/useProgressBarForm';
import {
  DEFAULT_OFFER_DESIGN_EXTERNAL_ID,
  PROMOTION_MODEL_BADGE_MAP,
  TRANSLATE_MOCK_MAP
} from 'views/Offers/Offer.consts';
import {
  buildBaseOfferPayload,
  buildOfferBadges,
  buildSchedulePayload,
  buildSectionTabPayload,
  checkUniqueness,
  buildBootInfo,
  buildFormTitle,
  buildProductsListForPreview,
  clampPriceValue,
  getRelevantBadgeType,
  mapApiProductsToLocal
} from 'views/Offers/Offer.utils';
import {
  descriptionFieldSchema,
  discountFieldSchema,
  freePriceFieldSchema,
  nameFieldSchema,
  offerUiIdFieldSchema,
  publisherOfferIdFieldSchema,
  salePercentageFieldSchema,
  tagsFieldSchema
} from 'views/Offers/Offer.validation';
import * as yup from 'yup';

import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import FormSectionsTabs from 'components/FormSectionsTabs';
import { generatePlayerData } from 'components/TemplatesStudio/Components/FormPage/Preview/utils/playerData';
import { Tabs, TabsContent } from 'components/ui/tabs';

import { OfferPreviewSection } from 'views/Offers/components/OfferPreviewSection';
import {
  MAP_OFFER_MODEL_TO_HAS_ONE_MAIN,
  PROMOTIONS_TABS
} from './PromotionsForm.consts';
import { useOfferPreviewData } from 'views/Offers/hooks/useOfferPreviewData';

import {
  buildCurrentPromotion,
  buildOfferData,
  buildPromotionFormInitialValues
} from './PromotionsForm.utils';
import ScheduleTab from './components/Tabs/ScheduleTab';
import SettingsTab from './components/Tabs/SettingsTab';

import 'style/forms.scss';


interface IPromotionsForm {
  edit?: boolean;
  dup?: boolean;
}

const PromotionsForm: React.FC<IPromotionsForm> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { specialOfferId } = useParams();
  const { canUserEdit } = usePermissions();

  const { updateOffer, addOffer, getOffer, getOffers } = useOffers({
    offerId: specialOfferId,
    offerType: OfferType.SPECIAL_OFFER
  });
  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Promotion',
    addOffer,
    updateOffer
  });
  const { data, isLoading, chosenSegment, setChosenSegment } = useOfferFormData({
    offerId: specialOfferId,
    publisherId,
    getOffer,
    onDataLoaded: (result: Partial<Offer>): void => {
      setCurrentOfferUI(result.offerUi?.offerUiId || '');
      setProducts(
        mapApiProductsToLocal(result.productsSequence?.[0]?.products as any[] || [])
      );
      setProgressBarFormValues({
        progressBarPoints:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.points || null,
        progressBarId:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.barId || null
      });
    }
  });
  const [tab, setTab] = useState(EPromotionsFormState.GENERAL);
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [dirtyProducts, setDirtyProducts] = useState(false);
  const [startHour, setStartHour] = useState<string | undefined>('');
  const [intervalCron, setIntervalCron] = useState<string>('');
  const {
    progressBarFormValues,
    setProgressBarFormValues,
    isProgressBarFormValid,
    dirtyProgressBar,
    handleProgressBarFormChange
  } = useProgressBarForm();

  const { getOffers: getProgressBars } = useOffers({
    offerType: OfferType.PROGRESS_BAR
  });
  const {
    offerDesignOptions,
    defaultOfferDesignId,
    getOfferUI,
    setCurrentOfferUI
  } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.SpecialOffer,
    defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.SPECIAL_OFFER],
    offerId: specialOfferId
  });
  const { getGeneralTheme: generalTheme, getStoreTheme } =
    useTheme(publisherId);
  const viewModel = generalTheme.data?.bundlesViewModel as EBundlesViewModel;
  const { featureFlags } = useFeatureFlags({ publisherId });

  const hasFeatureFlagTags = featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];

  const hasScheduleOfferFeatureFlag =
    featureFlags?.[EFeatureFlag.DASHBOARD_SCHEDULED_OFFERS];

  const isUnique = useCallback(
    (value: string | undefined, field: keyof Offer): boolean =>
      checkUniqueness(value, field, getOffers.data?.offers, data, !!edit),
    [getOffers.data?.offers, data, edit]
  );

  const { handleTabChange } = useTabTracking({
    sidebarTab: Promotions.title,
    tabs: PROMOTIONS_TABS,
    customNavigate: (value) => setTab(value as EPromotionsFormState)
  });

  const offerSchema = useMemo(
    () =>
      yup.object().shape({
        setAsFree: yup.boolean(),
        name: nameFieldSchema(isUnique),
        description: descriptionFieldSchema(),
        offerUiId: offerUiIdFieldSchema(),
        publisherOfferId: publisherOfferIdFieldSchema(isUnique),
        price: freePriceFieldSchema(),
        discount: discountFieldSchema(),
        priceBeforeDiscount: yup
          .number()
          .min(0, 'Price before discount cannot be negative')
          .nullable(),
        playerAvailability: yup
          .number()
          .min(1, 'Availability must be at least 1')
          .max(99, 'Availability cannot exceed 99')
          .when(['setAsFree', 'schedule.interval', 'startHour'], {
            is: (setAsFree: boolean, interval: string, startHour: string) =>
              setAsFree || interval || startHour,
            then: yup
              .number()
              .required(
                'Player availability is required when the offer is set as free, or if the offer is permanent with intervals'
              ),
            otherwise: yup.number()
          })
          .max(99, 'Availability cannot exceed 99'),
        startHour: yup
          .string()
          .test('is-empty', 'Start Hour is required', (value) => value !== '')
          .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Start Hour is required'),
        salePercentage: salePercentageFieldSchema(),
        tags: tagsFieldSchema()
      }),
    [isUnique]
  );

  const bundleModelFromTheme = useMemo(
    (): ESpecialOfferInternalViewModel | undefined =>
      generalTheme.data?.specialOffersInternalViewModel,
    [generalTheme.data?.specialOffersInternalViewModel]
  );

  const relevantBadgeType: EBadgeType | 'both' = useMemo(
    () => getRelevantBadgeType(bundleModelFromTheme, PROMOTION_MODEL_BADGE_MAP, viewModel),
    [viewModel, bundleModelFromTheme]
  );

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: {
      ...buildPromotionFormInitialValues({
        data,
        edit,
        dup,
        defaultOfferDesignId,
        getOfferResult: getOffer?.data?.result
      }),
      badge: data?.badges?.find((b) => {
        if (!b.badge?.type) return false;
        return filterRelevantBadges(b.badge.type);
      })?.badge?.badgeId || null,
      publisherBadgeId: data?.badges?.find((b) => {
        if (!b.badge?.type) return false;
        return filterRelevantBadges(b.badge.type);
      })?.badge?.publisherBadgeId || ''
    },
    onSubmit: async (values) => {
      const progressBarPoints = convertProgressBarPointsToApiFormat(
        progressBarFormValues
      );
      const offerBadges = buildOfferBadges(
        values.badge,
        values.publisherBadgeId,
        badges[0]?.badge?.publisherBadgeId || ''
      );
      const { sectionId, tabId } = buildSectionTabPayload(
        values.section,
        values.tab
      );
      const newOffer: Partial<Offer> & {
        sectionId?: string | null;
        tabId?: string | null;
      } = {
        ...buildBaseOfferPayload(values, chosenSegment, OfferType.SPECIAL_OFFER),
        description: values.description || null,
        coolDownInHours: 1,
        schedule: buildSchedulePayload(values.schedule),
        badges: offerBadges,
        productSale: {
          sale: values.salePercentage || 0,
          type:
            values.salePercentageDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        priceDiscount: {
          priceBeforeDiscount: values.priceBeforeDiscount
            ? values.priceBeforeDiscount * 100
            : null,
          discount: values.discount,
          type:
            values.priceDiscountDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        productsSequence: [
          {
            index: 1,
            progressBarPoints,
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.amount,
              ...(p.publisherProductId ? { publisherProductId: p.publisherProductId } : {}),
              priority: p.priority
            })),
            priceInUsdCents: values.setAsFree
              ? 0
              : new Decimal(values.price).mul(100).toNumber(),
            ...(values.playerAvailability
              ? { playerAvailability: values.playerAvailability }
              : undefined)
          }
        ],
        sectionId,
        tabId
      };

      submitOffer(newOffer, specialOfferId);
    }
  });

  const {
    badges,
    emblemBadges,
    ribbonBadges,
    badgeList,
    filterRelevantBadges
  } = useBadgeForm({
    publisherId,
    relevantBadgeType,
    viewModel,
    selectedBadgeId: values.badge
  });

  const progressBarOffers = useMemo(
    (): Offer[] => getProgressBars.data?.offers ?? [],
    [getProgressBars.data]
  );

  useUnsavedChanges({ dirty });
  const transformPriceValue = useCallback(
    (): number => clampPriceValue(values.price),
    [values.price]
  );

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  };

  const offersViewModel: ESpecialOfferInternalViewModel = useMemo(
    () =>
      generalTheme.data?.specialOffersInternalViewModel ||
      ESpecialOfferInternalViewModel.HUGE,
    [generalTheme.data]
  );

  const productsListForPreview = useMemo(() => {
    const list = buildProductsListForPreview(products as IProductProps[]);
    const hasOneMain = MAP_OFFER_MODEL_TO_HAS_ONE_MAIN[offersViewModel];

    if (hasOneMain && list.length > 0) {
      const isMainProductExist = list.some(
        (p) => p.priority === EProductPriority.MAIN
      );
      if (!isMainProductExist) {
        list[0].priority = EProductPriority.MAIN;
      }
    }

    return list;
  }, [products, offersViewModel]);

  const formTitle = useMemo(
    (): string => buildFormTitle(edit, 'Promotion', getOffer.data?.result?.name),
    [edit, getOffer.data?.result?.name]
  );

  const hasUnsavedChanges = useMemo(
    (): boolean =>
      !edit || dirty || dirtyProducts || dirtyProgressBar,
    [edit, dirty, dirtyProducts, dirtyProgressBar]
  );

  const isPermanentIntervalMissingStartHour =
    values?.schedule?.permanent &&
    Boolean(values?.schedule?.interval) &&
    !values?.startHour;

  const isScheduledOfferMissingTimeFrames =
    hasScheduleOfferFeatureFlag &&
    !values?.schedule?.permanent &&
    !values?.schedule?.timeFrames?.length;

  const isFormValid = useMemo(
    (): boolean =>
      !isLoading &&
      isValid &&
      isProgressBarFormValid &&
      hasUnsavedChanges &&
      canUserEdit &&
      !isPermanentIntervalMissingStartHour &&
      !isScheduledOfferMissingTimeFrames,
    [
      isLoading,
      isValid,
      isProgressBarFormValid,
      hasUnsavedChanges,
      canUserEdit,
      isPermanentIntervalMissingStartHour,
      isScheduledOfferMissingTimeFrames
    ]
  );

  const bootInfo = useMemo(
    () => buildBootInfo(getStoreTheme.data, featureFlags),
    [getStoreTheme.data, featureFlags]
  );

  const dynamicOfferData = useOfferPreviewData({
    values,
    getOfferUI,
    productsListForPreview,
    badges,
    emblemBadges,
    ribbonBadges,
    transformPriceValue,
    buildCurrentOffer: buildCurrentPromotion,
    buildOfferData
  });

  const previewProps = useMemo(
    () => ({
      translate: (key: string): string => TRANSLATE_MOCK_MAP[key] || key,
      playerData: generatePlayerData(),
      boot: bootInfo,
      offers: dynamicOfferData
    }),
    [bootInfo, dynamicOfferData]
  );

  const handleSave = useCallback((): void => {
    submitForm();
  }, [submitForm]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={
        <OfferPreviewSection
          previewProps={previewProps}
          storeThemeData={getStoreTheme.data}
          enableDeviceToggle
        />
      }
    >
      <Tabs
            className="h-full pt-10 pb-10 w-[528px]"
            value={tab}
            onValueChange={handleTabChange}
          >
            <FormSectionsTabs
              currentTab={tab}
              tabs={[
                {
                  value: EPromotionsFormState.GENERAL,
                  label: 'Settings',
                  disabled: getOffer.isLoading && edit
                },
                ...(hasScheduleOfferFeatureFlag
                  ? [
                      {
                        value: EPromotionsFormState.SCHEDULE,
                        label: 'Schedule',
                        disabled: getOffer.isLoading && edit
                      }
                    ]
                  : [])
              ]}
            />
            <div className="flex-1 flex justify-center">
              <TabsContent
                value={EPromotionsFormState.GENERAL}
                className="mt-9 w-full"
              >
                <SettingsTab
                  formikProps={formikProps}
                  values={values}
                  setCurrentOfferUI={setCurrentOfferUI}
                  chosenSegment={chosenSegment}
                  setChosenSegment={setChosenSegment}
                  hasFeatureFlagTags={hasFeatureFlagTags}
                  specialOfferId={specialOfferId}
                  products={products}
                  setProducts={setProducts}
                  edit={edit}
                  relevantBadgeType={relevantBadgeType}
                  badgeList={badgeList}
                  setDirtyProducts={setDirtyProducts}
                  setStartHour={setStartHour}
                  offerDesignOptions={offerDesignOptions}
                  dup={dup}
                  progressBarOffers={progressBarOffers}
                  getProgressBars={getProgressBars}
                  handleProgressBarFormChange={handleProgressBarFormChange}
                  progressBarFormValues={progressBarFormValues}
                />
              </TabsContent>
              {hasScheduleOfferFeatureFlag && (
                <TabsContent
                  value={EPromotionsFormState.SCHEDULE}
                  className={`mt-9 w-full`}
                >
                  <ScheduleTab
                    formikProps={formikProps}
                    values={values}
                    updateOffer={updateOffer}
                    edit={edit}
                    startHour={startHour}
                    setStartHour={setStartHour}
                    intervalCron={intervalCron}
                    setIntervalCron={setIntervalCron}
                  />
                </TabsContent>
              )}
            </div>
          </Tabs>
    </OfferFormLayout>
  );
};

export default PromotionsForm;
